// Validation utilities
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateEmailOrUsername = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.includes('@')) {
    return validateEmail(trimmed);
  }
  return true;
};

export const validatePasswordMinLength = (password: string): { valid: boolean; errors: string[] } => {
  if (password.length < 8) {
    return { valid: false, errors: ['Password must be at least 8 characters'] };
  }
  return { valid: true, errors: [] };
};

export const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s-]{10,}$/;
  return phoneRegex.test(phone);
};

// Constants
export const FIELD_OF_WORK_OTHER = 'other';

export const FIELD_OF_WORK_OPTIONS = [
  { value: 'careworker', label: 'Careworker' },
  { value: 'nurse_assistant', label: 'Nurse Assistant' },
  { value: 'home_caregiver', label: 'Home Caregiver' },
  { value: FIELD_OF_WORK_OTHER, label: 'Other' },
];

export const JOB_TITLE_OTHER = 'other';

export const JOB_TITLE_OPTIONS = [
  { value: 'Care Worker', label: 'Care Worker' },
  { value: 'Nurse Assistant', label: 'Nurse Assistant' },
  { value: 'Home Caregiver', label: 'Home Caregiver' },
  { value: JOB_TITLE_OTHER, label: 'Other' },
];

export const getFieldOfWorkLabel = (value: string): string => {
  const option = FIELD_OF_WORK_OPTIONS.find(opt => opt.value === value);
  return option?.label || value;
};

/** Map a stored field of work to select value + optional custom text for "Other". */
export function resolveFieldOfWorkSelection(fieldOfWork: string): {
  selection: string;
  customValue: string;
} {
  if (!fieldOfWork) return { selection: '', customValue: '' };
  const preset = FIELD_OF_WORK_OPTIONS.find(
    opt => opt.value !== FIELD_OF_WORK_OTHER && opt.value === fieldOfWork,
  );
  if (preset) return { selection: preset.value, customValue: '' };
  if (fieldOfWork === FIELD_OF_WORK_OTHER) {
    return { selection: FIELD_OF_WORK_OTHER, customValue: '' };
  }
  return { selection: FIELD_OF_WORK_OTHER, customValue: fieldOfWork };
}

/** Resolve select + custom Other text into the value saved as field_of_work. */
export function resolveFieldOfWorkValue(selection: string, customValue: string): string {
  if (selection === FIELD_OF_WORK_OTHER) return customValue.trim();
  return selection;
}

/** Map a stored job title to select value + optional custom text for "Other". */
export function resolveJobTitleSelection(jobTitle: string): {
  selection: string;
  customTitle: string;
} {
  if (!jobTitle) return { selection: '', customTitle: '' };
  const preset = JOB_TITLE_OPTIONS.find(
    opt => opt.value !== JOB_TITLE_OTHER && opt.value === jobTitle,
  );
  if (preset) return { selection: preset.value, customTitle: '' };
  return { selection: JOB_TITLE_OTHER, customTitle: jobTitle };
}

/** Resolve select + custom Other text into the value saved as job_title. */
export function resolveJobTitleValue(selection: string, customTitle: string): string {
  if (selection === JOB_TITLE_OTHER) return customTitle.trim();
  return selection;
}

export const MAX_FILE_SIZE_MB = 5;

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

export const ALLOWED_DOCUMENT_TYPES = ['application/pdf', ...ALLOWED_IMAGE_TYPES];

// Formatting utilities
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// String utilities
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatLabel = (value: string): string => {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

export const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || 'U';
};

export {
  isQualificationEvidenceVerified,
  isReferenceEvidenceVerified,
  getActiveQualificationVerification,
  sortQualificationsByIssueDate,
} from './qualificationStatus';

export {
  VERIFICATION_STATUS_LABEL,
  RECORD_KIND_LABEL,
  RECORD_KIND_PILL,
  RECORD_KIND_ACCENT,
  recordStatus,
  isAwaitingConfirmation,
  isAtLeastConfirmed,
  isVerified,
  homePath,
  PASSPORT_TAB_BY_KIND,
  passportItemPath,
  passportItemAnchor,
} from './verification';

export {
  siteHue,
  findTenant,
  findSite,
  tenantDisplayName,
  siteDisplayName,
  sitesForTenant,
} from './sites';

export { getPublicAppOrigin } from './publicAppUrl';

export {
  SHARE_EXPIRY_OPTIONS,
  DEFAULT_SHARE_EXPIRY_DAYS,
  getShareExpiryLabel,
  formatShareTimeRemaining,
  isShareExpired,
  buildShareUrl,
  openWhatsAppShare,
} from './shareHelpers';

export {
  PUBLIC_PASSPORT_FIELD_OPTIONS,
  DEFAULT_PUBLIC_PASSPORT_FIELDS,
  buildPublicPassportUrl,
  employeeQrFileSlug,
  generatePassportQrPng,
  generatePassportQrSvg,
  downloadDataUrl,
  downloadBlob,
  csvEscape,
  buildPublicPassportCsv,
} from './publicPassport';

export {
  formatAppVersionLabel,
  hasSeenRelease,
  markReleaseSeen,
  releaseSeenKey,
  shouldShowReleaseNotes,
} from './appRelease';

export {
  accountTypeFromRoles,
  avatarInitial,
  displayUserName,
  roleLabel,
  rolesFromClaims,
  userEmail,
} from './userDisplay';

export { getCroppedAvatarFile, loadImage } from './cropImage';
export type { PixelCrop } from './cropImage';

// Image optimization utilities
export {
  optimizeImage,
  generateSrcSet,
  getOptimalFormat,
  preloadImage,
  lazyLoadImage,
} from './imageOptimization';